import { offerVersionEndpoints } from '@ooh/contracts';
import type { LockPreviewRequest } from '@ooh/contracts';
import { useApiQuery } from '../../../data/use-api-query';
import { unitDecisionsRequest } from './queries';

interface PreviewOptions {
  enabled?: boolean;
}

function previewState<TData>(query: {
  isLoading: boolean;
  isError: boolean;
  data: TData | undefined;
  refetch: () => Promise<unknown>;
}) {
  return {
    isLoading: query.isLoading,
    isError: query.isError,
    data: query.data,
    refetch: () => void query.refetch(),
  };
}

export function useWithdrawalPreview(
  offerId: string,
  versionId: number,
  { enabled = true }: PreviewOptions = {},
) {
  return previewState(
    useApiQuery(offerVersionEndpoints.withdrawalPreview, unitDecisionsRequest(offerId, versionId), {
      enabled,
      suppressGlobalErrorToast: true,
    }),
  );
}

export function useRemoveRejectedPreview(
  offerId: string,
  versionId: number,
  { enabled = true }: PreviewOptions = {},
) {
  return previewState(
    useApiQuery(
      offerVersionEndpoints.removeRejectedPreview,
      unitDecisionsRequest(offerId, versionId),
      { enabled, suppressGlobalErrorToast: true },
    ),
  );
}

export function useLockPreview(
  offerId: string,
  versionId: number,
  status: LockPreviewRequest['status'],
  { enabled = true }: PreviewOptions = {},
) {
  return previewState(
    useApiQuery(
      offerVersionEndpoints.lockPreview,
      { ...unitDecisionsRequest(offerId, versionId), status },
      { enabled, suppressGlobalErrorToast: true },
    ),
  );
}

export function useReleaseRejectedPreview(
  offerId: string,
  versionId: number,
  { enabled = true }: PreviewOptions = {},
) {
  return previewState(
    useApiQuery(
      offerVersionEndpoints.releaseRejectedPreview,
      unitDecisionsRequest(offerId, versionId),
      { enabled, suppressGlobalErrorToast: true },
    ),
  );
}

// Non-suspending: an empty or failed lookup must not unmount the dialog or the finalization panel around it.
export function useUnitDecisions(
  offerId: string,
  versionId: number,
  { enabled = true }: PreviewOptions = {},
) {
  return useApiQuery(
    offerVersionEndpoints.unitDecisions,
    unitDecisionsRequest(offerId, versionId),
    {
      enabled,
    },
  );
}
